import { stores } from '@kurtosys/app-start';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { FilterPropertyStore } from '../../App/stores/FilterPropertyStore.js';
import { GlobalInputsStore } from '../../App/stores/GlobalInputsStore.js';
import { InputStoreBase } from '../../shared/InputStoreBase.js';
import { IDatePickerComponentProps, IDatePickerOptions } from '../models/index.js';

export class DatePickerStore extends InputStoreBase<IDatePickerOptions, IDatePickerComponentProps, string> {
	static componentKey = 'datePicker' as const;
	@observable allowedValues: string[] | undefined;

	constructor(
		storeContext: TStoreContext,
		manifest: IManifest,
		inputKey: string,
		options: IDatePickerOptions,
		componentProps: IDatePickerComponentProps,
	) {
		super(storeContext, manifest, inputKey, options, componentProps);
		makeObservable(this);
	}

	initializeItems = async () => {
		this.getAllowedValues();
		this.initializeValue();
	};

	@computed
	get hasData(): boolean {
		// this.options.allowedValues validates if we have config for the allowed values. If it is undefined or empty, we do not have data.
		// if we do not configure allowed values, we assume we have data, as it will show a full calendar.
		if (this.options.allowedValues && (!this.allowedValues || this.allowedValues.length === 0)) {
			return false;
		}

		return true;
	}

	@computed
	get filterPropertyStore(): FilterPropertyStore {
		return this.storeContext.get<FilterPropertyStore>('filterPropertyStore');
	}

	initializeValue = (): void => {
		const appStore = this.storeContext.get<AppStore>('appStore');
		const globalInputsStore = this.storeContext.get<GlobalInputsStore>('globalInputsStore');
		let value = '';
		// Get default value from query
		if (this.options.defaultValue) {
			value = this.getQueryValue(this.options.defaultValue);
		}

		if (appStore.loadInitialValuesFromStorage) {
			const valueFromStorage = globalInputsStore.getValue(this.inputKey);
			if (valueFromStorage) {
				// Update value if there is a value in storage
				value = valueFromStorage;
			}
		}
		this.setValue(value);
	};

	@action
	setValue = (value: string) => {
		const { filter } = this.options;
		this.value = value;
		const globalInputsStore = this.storeContext.get<GlobalInputsStore>('globalInputsStore');
		let usingFilters = false;
		if (filter) {
			const filterValues = this.filterPropertyStore.getFilterPropertyValues(filter);
			const filterValue = filterValues.find((f) => f.value === value);
			if (filterValue) {
				this.filterPropertyStore.updateFilterValueSelections(filter, [filterValue]);
				usingFilters = true;
			}
		}
		globalInputsStore.setValue(this.inputKey, value, !usingFilters);
	};

	formatValue = (value: string): string => {
		const translationStore =
			this.storeContext.get<stores.TranslationStore<IConfiguration, IComponentStyles>>('translationStore');
		const { valueFormat } = this.options;
		if (valueFormat) {
			return common.commonUtils.formatDateTime(
				translationStore.culture,
				valueFormat,
				value,
				translationStore.translate,
			) as string;
		}
		return value;
	};

	@computed
	get labelValue(): string {
		const { label, labelQuery } = this.options;
		let value: string = label || '';
		if (labelQuery) {
			value = this.getQueryValue(labelQuery);
		}
		return value;
	}

	filterValues = (): string[] => {
		const { filter } = this.options;
		const filterValues = this.filterPropertyStore.getFilterPropertyValues(filter);
		const values: string[] = [];
		if (filterValues) {
			for (const filterValue of filterValues) {
				if (Array.isArray(filterValue.value)) {
					values.push(...filterValue.value);
				} else {
					values.push(filterValue.value);
				}
			}
		}
		return values;
	};
	@action
	async getAllowedValues() {
		const { allowedValues, filter } = this.options;
		let value;
		if (allowedValues) {
			value = this.getQueryValue(allowedValues);
			if (typeof value === 'string') {
				value = [value];
			}
			this.allowedValues = value;
		}
		if (filter) {
			this.allowedValues = this.filterValues();
		}
	}

	@computed
	get minAllowedValue(): string {
		if (this.allowedValues) {
			return this.allowedValues.reduce((a, b) => {
				return new Date(a) < new Date(b) ? a : b;
			}, '');
		}
		return '';
	}

	@computed
	get maxAllowedValue(): string {
		if (this.allowedValues) {
			return this.allowedValues.reduce((a, b) => {
				return new Date(a) > new Date(b) ? a : b;
			}, '');
		}
		return '';
	}
}
